import { useState, useCallback, useMemo, useEffect } from "react";
import { DistinctValue } from "../../../types";

/**
 * Custom hook for managing checkbox state for distinct values.
 * Handles individual checkbox toggling, select all, and derived states.
 */
const useCheckboxState = (
  mainValues: DistinctValue[],
  selectedCategory: number,
) => {
  const [checked, setChecked] = useState<{ [key: string]: boolean }>({});

  // Reset checked values when category changes
  useEffect(() => {
    setChecked({});
  }, [selectedCategory]);

  // Drop checks for values that are no longer listed. Completing, purging or
  // moving values refreshes the list without them, and the footers count every
  // checked id, so stale ids kept showing the old selection count.
  useEffect(() => {
    setChecked((prev) => {
      const listedIds = new Set(mainValues.map((val) => val.id));
      const kept = Object.fromEntries(
        Object.entries(prev).filter(([id]) => listedIds.has(id)),
      );
      // Keep the same object when nothing changed, to avoid a re-render loop.
      return Object.keys(kept).length === Object.keys(prev).length
        ? prev
        : kept;
    });
  }, [mainValues]);

  const handleSelectAll = useCallback(
    (isChecked: boolean) => {
      const newChecked: { [key: string]: boolean } = {};
      mainValues.forEach((val) => {
        newChecked[val.id] = isChecked;
      });
      setChecked((prev) => ({ ...prev, ...newChecked }));
    },
    [mainValues],
  );

  const handleCheckValue = useCallback(
    (valueId: string, isChecked: boolean) => {
      setChecked((prev) => ({
        ...prev,
        [valueId]: isChecked,
      }));
    },
    [],
  );

  const clearChecked = useCallback((valueId: string) => {
    setChecked((prev) => {
      const newChecked = { ...prev };
      delete newChecked[valueId];
      return newChecked;
    });
  }, []);

  const allChecked = useMemo(
    () => mainValues.every((val) => checked[val.id]) && mainValues.length > 0,
    [mainValues, checked],
  );

  const indeterminate = useMemo(
    () =>
      mainValues.some((val) => checked[val.id]) &&
      !mainValues.every((val) => checked[val.id]),
    [mainValues, checked],
  );

  return {
    checked,
    handleSelectAll,
    handleCheckValue,
    clearChecked,
    allChecked,
    indeterminate,
  };
};

export default useCheckboxState;
